<div class="live-display-container" [class.is-kiosk-mode]="isKiosk">
  <!-- Top Header Bar -->
  <header class="live-display-header">
    <div class="header-left">
      <div class="brand-badge">
        <span class="pulse-indicator"></span>
        <span class="brand-title">TiCCBox LIVE ENERGY</span>
      </div>
      <span class="header-subtitle">Dual-Utility Energy Dashboard</span>
    </div>

    <div class="header-right">
      <div class="header-stat">
        <span class="stat-label">AUTO REFRESH</span>
        <span class="stat-value">30s</span>
      </div>
      <div class="header-stat">
        <span class="stat-label">LAST UPDATED</span>
        <span class="stat-value">{{ lastRefreshedAt | date:'HH:mm:ss' }}</span>
      </div>
      <button class="action-btn" (click)="toggleFullscreen()" title="Toggle Fullscreen (F)">
        <span class="icon">⛶</span>
        <span class="btn-text">{{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}</span>
      </button>
    </div>
  </header>

  <!-- Error Banner -->
  <div class="error-banner" *ngIf="errorMessage">
    <span class="error-icon">⚠️</span>
    <span>{{ errorMessage }}</span>
  </div>

  <!-- Loading State -->
  <div class="loading-state" *ngIf="isLoading && !electricityPanel && !gasPanel">
    <div class="spinner"></div>
    <p>Connecting to Live Energy Stream...</p>
  </div>

  <!-- Dual Utility Layout (50/50 Screen) -->
  <main class="dual-utility-view" *ngIf="electricityPanel || gasPanel">
    <div class="dual-utility-grid">

      <!-- ==================== LEFT PANEL: ELECTRICITY ==================== -->
      <section class="utility-panel electricity-panel" *ngIf="electricityPanel as elec">
        <!-- Panel Header -->
        <div class="panel-header">
          <div class="utility-title-group">
            <span class="utility-icon">⚡</span>
            <div>
              <h2 class="utility-title">{{ elec.config.name || 'Electricity' }}</h2>
              <span class="utility-type-tag">ELECTRICITY</span>
            </div>
          </div>
          <span class="status-badge" [ngClass]="getStatusClass(elec.config.status)">
            {{ elec.config.status }}
          </span>
        </div>

        <!-- Hero KPIs Row (3 Metric Cards) -->
        <div class="kpi-grid">
          <!-- KPI 1: Today So Far -->
          <div class="kpi-card hero-kpi">
            <span class="kpi-label">TODAY SO FAR</span>
            <div class="kpi-value-wrap">
              <span class="kpi-num">{{ formatValue(elec.config.todayValue) }}</span>
              <span class="kpi-unit" *ngIf="elec.config.unit">{{ elec.config.unit }}</span>
            </div>
            <span class="kpi-cost" *ngIf="elec.config.todayCost !== null && elec.config.todayCost !== undefined">
              {{ formatCost(elec.config.todayCost, elec.config.currency) }}
            </span>
            <span class="kpi-sub" *ngIf="elec.config.valueDataThrough">Through {{ elec.config.valueDataThrough }} today</span>
            <span class="kpi-sub" *ngIf="!elec.config.valueDataThrough">Since midnight</span>
          </div>

          <!-- KPI 2: Same Day Last Week -->
          <div class="kpi-card baseline-kpi">
            <span class="kpi-label">{{ getBaselineWeekdayLabel(elec.config) | uppercase }}</span>
            <div class="kpi-value-wrap">
              <span class="kpi-num">{{ formatValue(elec.config.baselineSameTimeValue) }}</span>
              <span class="kpi-unit" *ngIf="elec.config.unit">{{ elec.config.unit }}</span>
            </div>
            <span class="kpi-cost" *ngIf="elec.config.baselineCost !== null && elec.config.baselineCost !== undefined">
              {{ formatCost(elec.config.baselineCost, elec.config.currency) }}
            </span>
            <span class="kpi-sub">7 days ago (to same interval)</span>
          </div>

          <!-- KPI 3: Difference & % Change -->
          <div class="kpi-card diff-kpi">
            <span class="kpi-label">DIFFERENCE</span>
            <div class="kpi-value-wrap">
              <span class="kpi-num diff-num" [ngClass]="getDeltaClass(elec.config.differenceValue, elec.config.changePercent)">
                {{ formatDifference(elec.config.differenceValue, elec.config.unit) }}
              </span>
            </div>
            <span class="kpi-cost diff-cost" *ngIf="elec.config.differenceCost !== null && elec.config.differenceCost !== undefined">
              {{ formatDifferenceCost(elec.config.differenceCost, elec.config.currency) }}
            </span>
            <div class="kpi-delta" [ngClass]="getDeltaClass(elec.config.differenceValue, elec.config.changePercent)">
              <span class="delta-arrow" *ngIf="elec.config.changePercent !== null && elec.config.changePercent !== undefined">
                {{ elec.config.changePercent > 0 ? '▲' : elec.config.changePercent < 0 ? '▼' : '–' }}
              </span>
              <span>{{ formatPercent(elec.config.changePercent) }}</span>
            </div>
          </div>
        </div>

        <!-- Cumulative Line Chart -->
        <div class="chart-section">
          <div class="chart-header">
            <div>
              <h3>Cumulative Consumption Profile</h3>
              <p class="chart-sub">Intra-day running total vs. {{ getBaselineWeekdayLabel(elec.config) | lowercase }}</p>
            </div>
            <div class="chart-legend-custom">
              <div class="legend-item"><span class="legend-box color-elec-today"></span> Today ({{ elec.config.unit || 'kWh' }})</div>
              <div class="legend-item"><span class="legend-box color-baseline"></span> {{ getBaselineWeekdayLabel(elec.config) }}</div>
            </div>
          </div>

          <div class="chart-canvas" *ngIf="elec.chartResults.length > 0">
            <ngx-charts-line-chart
              [scheme]="elec.chartColorScheme"
              [results]="elec.chartResults"
              [xAxis]="true"
              [yAxis]="true"
              [xAxisTicks]="xAxisTicks"
              [xAxisTickFormatting]="xAxisTickFormatting"
              [rotateXAxisTicks]="false"
              [trimXAxisTicks]="false"
              [legend]="false"
              [showXAxisLabel]="false"
              [showYAxisLabel]="false"
              [autoScale]="true"
              [animations]="true"
              [timeline]="false"
            >
            </ngx-charts-line-chart>
          </div>

          <div class="no-chart-data" *ngIf="!elec.chartResults.length">
            <p>Awaiting 24-hour profile data...</p>
          </div>
        </div>

        <!-- Panel Footer / Freshness -->
        <div class="panel-footer">
          <div class="freshness-item">
            <span class="footer-label">LAST METER READING</span>
            <span class="footer-value">{{ elec.config.lastUpdated ? (elec.config.lastUpdated | date:'HH:mm:ss') : 'Awaiting data' }}</span>
          </div>
          <div class="freshness-item">
            <span class="footer-label">DATA THROUGH</span>
            <span class="footer-value">{{ elec.config.valueDataThrough ? elec.config.valueDataThrough : 'Awaiting data' }}</span>
          </div>
        </div>
      </section>

      <!-- ==================== RIGHT PANEL: GAS ==================== -->
      <section class="utility-panel gas-panel" *ngIf="gasPanel as gas">
        <!-- Panel Header -->
        <div class="panel-header">
          <div class="utility-title-group">
            <span class="utility-icon gas-icon">🔥</span>
            <div>
              <h2 class="utility-title">{{ gas.config.name || 'Gas' }}</h2>
              <span class="utility-type-tag gas-tag">GAS</span>
            </div>
          </div>
          <span class="status-badge" [ngClass]="getStatusClass(gas.config.status)">
            {{ gas.config.status }}
          </span>
        </div>

        <!-- Hero KPIs Row (3 Metric Cards) -->
        <div class="kpi-grid">
          <!-- KPI 1: Today So Far -->
          <div class="kpi-card hero-kpi gas-hero">
            <span class="kpi-label">TODAY SO FAR</span>
            <div class="kpi-value-wrap">
              <span class="kpi-num">{{ gas.config.isMapped ? formatValue(gas.config.todayValue) : '--' }}</span>
              <span class="kpi-unit" *ngIf="gas.config.isMapped && gas.config.unit">{{ gas.config.unit }}</span>
            </div>
            <span class="kpi-cost" *ngIf="gas.config.isMapped && gas.config.todayCost !== null && gas.config.todayCost !== undefined">
              {{ formatCost(gas.config.todayCost, gas.config.currency) }}
            </span>
            <span class="kpi-sub" *ngIf="gas.config.isMapped && gas.config.valueDataThrough">Through {{ gas.config.valueDataThrough }} today</span>
            <span class="kpi-sub" *ngIf="!gas.config.isMapped">{{ gas.config.statusMessage || 'Awaiting client gas mapping' }}</span>
          </div>

          <!-- KPI 2: Same Day Last Week -->
          <div class="kpi-card baseline-kpi">
            <span class="kpi-label">{{ getBaselineWeekdayLabel(gas.config) | uppercase }}</span>
            <div class="kpi-value-wrap">
              <span class="kpi-num">{{ gas.config.isMapped ? formatValue(gas.config.baselineSameTimeValue) : '--' }}</span>
              <span class="kpi-unit" *ngIf="gas.config.isMapped && gas.config.unit">{{ gas.config.unit }}</span>
            </div>
            <span class="kpi-cost" *ngIf="gas.config.isMapped && gas.config.baselineCost !== null && gas.config.baselineCost !== undefined">
              {{ formatCost(gas.config.baselineCost, gas.config.currency) }}
            </span>
            <span class="kpi-sub">{{ gas.config.isMapped ? '7 days ago (to same interval)' : 'Awaiting client gas mapping' }}</span>
          </div>

          <!-- KPI 3: Difference & % Change -->
          <div class="kpi-card diff-kpi">
            <span class="kpi-label">DIFFERENCE</span>
            <div class="kpi-value-wrap">
              <span class="kpi-num diff-num">
                {{ gas.config.isMapped ? formatDifference(gas.config.differenceValue, gas.config.unit) : '--' }}
              </span>
            </div>
            <span class="kpi-cost diff-cost" *ngIf="gas.config.isMapped && gas.config.differenceCost !== null && gas.config.differenceCost !== undefined">
              {{ formatDifferenceCost(gas.config.differenceCost, gas.config.currency) }}
            </span>
            <div class="kpi-delta" [ngClass]="getDeltaClass(gas.config.differenceValue, gas.config.changePercent)">
              <span class="delta-arrow" *ngIf="gas.config.isMapped && gas.config.changePercent !== null && gas.config.changePercent !== undefined">
                {{ gas.config.changePercent > 0 ? '▲' : gas.config.changePercent < 0 ? '▼' : '–' }}
              </span>
              <span>{{ gas.config.isMapped ? formatPercent(gas.config.changePercent) : '–' }}</span>
            </div>
          </div>
        </div>

        <!-- Cumulative Line Chart or Placeholder Area -->
        <div class="chart-section">
          <div class="chart-header">
            <div>
              <h3>Cumulative Consumption Profile</h3>
              <p class="chart-sub">Intra-day running total vs. {{ getBaselineWeekdayLabel(gas.config) | lowercase }}</p>
            </div>
            <div class="chart-legend-custom" *ngIf="gas.config.isMapped">
              <div class="legend-item"><span class="legend-box color-gas-today"></span> Today ({{ gas.config.unit || 'm³' }})</div>
              <div class="legend-item"><span class="legend-box color-baseline"></span> {{ getBaselineWeekdayLabel(gas.config) }}</div>
            </div>
          </div>

          <!-- Mapped Chart -->
          <div class="chart-canvas" *ngIf="gas.config.isMapped && gas.chartResults.length > 0">
            <ngx-charts-line-chart
              [scheme]="gas.chartColorScheme"
              [results]="gas.chartResults"
              [xAxis]="true"
              [yAxis]="true"
              [xAxisTicks]="xAxisTicks"
              [xAxisTickFormatting]="xAxisTickFormatting"
              [rotateXAxisTicks]="false"
              [trimXAxisTicks]="false"
              [legend]="false"
              [showXAxisLabel]="false"
              [showYAxisLabel]="false"
              [autoScale]="true"
              [animations]="true"
              [timeline]="false"
            >
            </ngx-charts-line-chart>
          </div>

          <!-- Unmapped Finished Placeholder Area -->
          <div class="placeholder-chart-box" *ngIf="!gas.config.isMapped">
            <div class="placeholder-content">
              <span class="placeholder-icon">📊</span>
              <h4>Awaiting Client Gas Mapping</h4>
              <p>Gas telemetry will appear when the client gas channel is mapped in configuration.</p>
            </div>
          </div>
        </div>

        <!-- Panel Footer / Freshness -->
        <div class="panel-footer">
          <div class="freshness-item">
            <span class="footer-label">LAST METER READING</span>
            <span class="footer-value">{{ gas.config.isMapped && gas.config.lastUpdated ? (gas.config.lastUpdated | date:'HH:mm:ss') : 'Awaiting meter integration' }}</span>
          </div>
          <div class="freshness-item">
            <span class="footer-label">DATA THROUGH</span>
            <span class="footer-value">{{ gas.config.isMapped && gas.config.valueDataThrough ? gas.config.valueDataThrough : '--' }}</span>
          </div>
        </div>
      </section>

    </div>
  </main>
</div>
